import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';

import { ApiError } from '@/api/client';
import {
  getCategoryNoticeConfig,
  saveCategoryNoticeConfig,
  type NoticeConfigValues,
} from '@/api/notices';
import type { TeamCategory } from '@/api/teamCategories';
import { Button } from '@/components/ui/button';
import { FormErrorCallout } from '@/components/FormErrorCallout';
import { NoticeConfigForm } from '@/components/notices/NoticeConfigForm';

// The "Aviseringar" tab of the team-category drawer: the notices DEFAULTS every
// team in the category inherits (flow, fees, try-out, self-dereg, reminders).
// Any field left on "inherit" falls through to the flow-profile default; teams
// override individual fields from their own view.

export interface CategoryNoticesTabProps {
  slug: string;
  category: TeamCategory;
  open: boolean;
  onClose: () => void;
}

export function CategoryNoticesTab({ slug, category, open, onClose }: CategoryNoticesTabProps) {
  const { t } = useTranslation('common');
  const auth = useAuth();
  const token = auth.user?.access_token ?? null;
  const queryClient = useQueryClient();

  const [values, setValues] = useState<NoticeConfigValues>({});
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errorTick, setErrorTick] = useState(0);

  const configQuery = useQuery({
    queryKey: ['category-notice-config', slug, category.id],
    queryFn: () => getCategoryNoticeConfig(token, slug, category.id),
    enabled: !!token && !!slug && open,
  });

  // Seed once per open.
  const seededRef = useRef(false);
  useEffect(() => {
    if (!open) {
      seededRef.current = false;
      return;
    }
    if (seededRef.current || !configQuery.data) return;
    setValues(configQuery.data.values);
    seededRef.current = true;
  }, [open, configQuery.data]);

  const mutation = useMutation({
    mutationFn: () => saveCategoryNoticeConfig(token, slug, category.id, values),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['category-notice-config', slug, category.id] });
      onClose();
    },
    onError: (err) => {
      setErrorCode(err instanceof ApiError ? err.code : 'unknown');
      setErrorTick((n) => n + 1);
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        setErrorCode(null);
        mutation.mutate();
      }}
      className="flex min-h-0 flex-1 flex-col"
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 sm:p-6">
        <p className="text-sm text-muted-foreground">{t('notices.config.categoryIntro')}</p>
        {errorCode && (
          <FormErrorCallout key={errorTick} heading={t('notices.config.error.heading')}>
            <p>{t('notices.config.error.generic')}</p>
          </FormErrorCallout>
        )}
        {configQuery.isPending ? (
          <p className="text-sm text-muted-foreground">{t('notices.config.loading')}</p>
        ) : (
          <NoticeConfigForm
            values={values}
            onChange={setValues}
            resolved={configQuery.data?.resolved}
            hideInherit
            disabled={mutation.isPending}
          />
        )}
      </div>
      <footer className="flex justify-end gap-2 border-t border-border bg-background p-4">
        <Button type="button" variant="outline" onClick={onClose} disabled={mutation.isPending}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={mutation.isPending || configQuery.isPending}>
          {mutation.isPending ? t('notices.config.saving') : t('notices.config.save')}
        </Button>
      </footer>
    </form>
  );
}
